import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { portalHttp } from '../lib/portal-api';

export interface PortalUser {
  id: string;
  email: string;
  name: string | null;
  company_id: string;
  company_name: string;
}

export function usePortalMe() {
  return useQuery({
    queryKey: ['portal', 'me'],
    queryFn: () => portalHttp.get<{ success: true; data: PortalUser }>('/api/portal/me'),
    retry: false,
    staleTime: 60_000,
  });
}

export function usePortalLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      portalHttp.post<{ success: true; data: PortalUser }>('/api/portal/auth/login', body),
    onSuccess: (res) => qc.setQueryData(['portal', 'me'], res),
  });
}

export function usePortalLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => portalHttp.post<{ success: true }>('/api/portal/auth/logout'),
    onSuccess: () => qc.setQueryData(['portal', 'me'], undefined),
  });
}

export function usePortalSetPassword() {
  return useMutation({
    mutationFn: (body: { token: string; password: string }) =>
      portalHttp.post<{ success: true }>('/api/portal/auth/set-password', body),
  });
}

export function usePortalForgotPassword() {
  return useMutation({
    mutationFn: (email: string) =>
      portalHttp.post<{ success: true }>('/api/portal/auth/forgot-password', { email }),
  });
}

export interface PortalPickupRequest {
  id: string;
  status: string;
  contact_name: string;
  address: string | null;
  phone: string | null;
  scheduled_date: string | null;
  time_window_start: string | null;
  time_window_end: string | null;
  created_at: string;
}

interface Paginated<T> {
  success: true;
  data: T[];
  meta: { total: number; page: number; page_size: number; total_pages: number };
}

export function usePortalPickupRequests(page = 1) {
  return useQuery({
    queryKey: ['portal', 'pickup-requests', page],
    queryFn: () => portalHttp.get<Paginated<PortalPickupRequest>>('/api/portal/pickup-requests', { page }),
  });
}

export function useCreatePortalPickupRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      contact_name: string;
      address?: string;
      phone?: string;
      instructions?: string;
      scheduled_date?: string;
      time_window_start?: string;
      time_window_end?: string;
    }) =>
      portalHttp.post<{ success: true; data: PortalPickupRequest }>('/api/portal/pickup-requests', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal', 'pickup-requests'] }),
  });
}

export interface PortalOfferItem {
  waste_name: string;
  quantity_kg: number;
  price_per_kg: number;
}

export interface PortalOffer {
  id: string;
  status: 'sent' | 'accepted' | 'rejected' | 'expired';
  valid_until: string | null;
  sent_at: string | null;
  pdf_available: boolean;
  total_value: number;
  items: PortalOfferItem[];
}

export function usePortalOffers(page = 1) {
  return useQuery({
    queryKey: ['portal', 'offers', page],
    queryFn: () => portalHttp.get<Paginated<PortalOffer>>('/api/portal/offers', { page }),
  });
}

export interface PortalContract {
  id: string;
  contract_number: string | null;
  valid_from: string | null;
  valid_until: string | null;
  is_signed: boolean;
}

export function usePortalContracts() {
  return useQuery({
    queryKey: ['portal', 'contracts'],
    queryFn: () => portalHttp.get<{ success: true; data: PortalContract[] }>('/api/portal/contracts'),
  });
}

export interface PortalTransaction {
  id: string;
  type: 'collection' | 'outgoing';
  number: string | null;
  date: string;
  total_kg: string | null;
}

export function usePortalTransactions(page = 1) {
  return useQuery({
    queryKey: ['portal', 'transactions', page],
    queryFn: () => portalHttp.get<Paginated<PortalTransaction>>('/api/portal/transactions', { page }),
  });
}

export interface PortalDocument {
  id: string;
  type: string;
  document_set_number: string | null;
  issue_date: string;
}

export function usePortalDocuments(page = 1) {
  return useQuery({
    queryKey: ['portal', 'documents', page],
    queryFn: () => portalHttp.get<Paginated<PortalDocument>>('/api/portal/documents', { page }),
  });
}
